import React, { useState } from 'react';
import { XIcon, ChevronDownIcon, ChevronRightIcon } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
interface MobileFilterModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedFilters: {
    brands: string[];
    priceRange: {
      min: number;
      max: number;
    };
    categories: string[];
    needsPrescription: boolean;
    isExpress: boolean;
    isGlobal: boolean;
    offers: string[];
  };
  onFilterChange: (filters: any) => void;
}
export function MobileFilterModal({
  isOpen,
  onClose,
  selectedFilters,
  onFilterChange
}: MobileFilterModalProps) {
  const [expandedSections, setExpandedSections] = useState({
    price: true,
    brands: true,
    categories: true,
    offers: true
  });
  const brands = ['Pampers', 'Huggies', 'Babyjoy', 'Fine Baby', 'Bambi', 'Molfix'];
  const categories = ['Baby Diapers', 'Training Pants', 'Swim Diapers', 'Overnight Diapers', 'Eco-Friendly Diapers'];
  const offers = ['On Sale', 'Special Offers', 'Buy One Get One', 'Clearance'];
  const toggleSection = (section: string) => {
    setExpandedSections({
      ...expandedSections,
      [section]: !expandedSections[section]
    });
  };
  const handleBrandToggle = (brand: string) => {
    const newBrands = selectedFilters.brands.includes(brand) ? selectedFilters.brands.filter(b => b !== brand) : [...selectedFilters.brands, brand];
    onFilterChange({
      ...selectedFilters,
      brands: newBrands
    });
  };
  const handleCategoryToggle = (category: string) => {
    const newCategories = selectedFilters.categories.includes(category) ? selectedFilters.categories.filter(c => c !== category) : [...selectedFilters.categories, category];
    onFilterChange({
      ...selectedFilters,
      categories: newCategories
    });
  };
  const handleOfferToggle = (offer: string) => {
    const newOffers = selectedFilters.offers.includes(offer) ? selectedFilters.offers.filter(o => o !== offer) : [...selectedFilters.offers, offer];
    onFilterChange({
      ...selectedFilters,
      offers: newOffers
    });
  };
  const handleReset = () => {
    onFilterChange({
      brands: [],
      priceRange: {
        min: 0,
        max: 5000
      },
      categories: [],
      needsPrescription: false,
      offers: []
    });
  };
  return <AnimatePresence data-id="element-1664" data-mp="10vg765wcsq19">
      {isOpen && <>
          {/* Overlay */}
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2
      }} className="fixed inset-0 bg-black bg-opacity-50 z-50" onClick={onClose} data-id="element-1665" data-mp="vwlbw4h79909" />
          {/* Bottom Sheet */}
          <motion.div initial={{
        y: '100%'
      }} animate={{
        y: 0
      }} exit={{
        y: '100%'
      }} transition={{
        type: 'spring',
        damping: 30,
        stiffness: 300
      }} className="fixed bottom-0 left-0 right-0 bg-white z-50 shadow-2xl rounded-t-2xl flex flex-col max-h-[90vh]" data-id="element-1666" data-mp="2hahe9a93gi0y">
            {/* Drag Handle */}
            <div className="flex justify-center pt-2 pb-1 flex-shrink-0" data-id="element-1667" data-mp="37jf27m159kj7">
              <div className="w-10 h-1 bg-gray-300 rounded-full" data-id="element-1668" data-mp="37mb23fwjyjaa" />
            </div>
            {/* Header */}
            <div className="bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between flex-shrink-0" data-id="element-1669" data-mp="2xu5d8f1lkhzb">
              <h2 className="text-lg font-bold text-gray-900" data-id="element-1670" data-mp="32sgkqj4f6ged">Filters</h2>
              <button onClick={onClose} className="p-2 rounded-full hover:bg-gray-100" aria-label="Close filters" data-id="element-1671" data-mp="3qisxc7am11ba">
                <XIcon className="w-5 h-5 text-gray-500" data-id="element-1672" data-mp="3vbm22v62w9nd" />
              </button>
            </div>
            {/* Filter Content - Scrollable */}
            <div className="flex-1 overflow-y-auto overscroll-contain" data-id="element-1673" data-mp="22lv6n2mfaaqm">
              {/* Price Filter */}
              <div className="border-b border-gray-100" data-id="element-1674" data-mp="1xo33tdk8p47n">
                <button onClick={() => toggleSection('price')} className="flex justify-between items-center w-full p-4 text-left" data-id="element-1675" data-mp="1snyo3pr5odih">
                  <h3 className="text-base font-bold text-gray-900" data-id="element-1676" data-mp="2qdsao7jmryh6">
                    Filter By Price
                  </h3>
                  {expandedSections.price ? <ChevronDownIcon className="w-5 h-5 text-gray-500" data-id="element-1677" data-mp="2gkbewxzxo9av" /> : <ChevronRightIcon className="w-5 h-5 text-gray-500" data-id="element-1678" data-mp="2bm9ua8mv01xa" />}
                </button>
                {expandedSections.price && <div className="px-4 pb-4" data-id="element-1679" data-mp="34ayrhwksnn3r">
                    <div className="flex justify-between mb-2 text-sm text-gray-700" data-id="element-1680" data-mp="34a5q7b4ltpt0">
                      <span data-id="element-1681" data-mp="3clpbbuuy9kqd">{selectedFilters.priceRange.min} SAR</span>
                      <span data-id="element-1682" data-mp="3gky52zpzpilx">— {selectedFilters.priceRange.max} SAR</span>
                    </div>
                    <div className="relative h-2 bg-gray-200 rounded-full" data-id="element-1683" data-mp="3lkjbtwbeiv97">
                      <div className="absolute h-full w-1/3 bg-[#0066CC] rounded-full" data-id="element-1684" data-mp="3qjm9uwsys17o"></div>
                    </div>
                  </div>}
              </div>
              {/* Brand Filter */}
              <div className="border-b border-gray-100" data-id="element-1685" data-mp="3eo5ol92jtb4">
                <button onClick={() => toggleSection('brands')} className="flex justify-between items-center w-full p-4 text-left" data-id="element-1686" data-mp="3d3cp02ekifd">
                  <h3 className="text-base font-bold text-gray-900" data-id="element-1687" data-mp="d47rnzs2w3a7">Brands</h3>
                  {expandedSections.brands ? <ChevronDownIcon className="w-5 h-5 text-gray-500" data-id="element-1688" data-mp="n36rqtys2qll" /> : <ChevronRightIcon className="w-5 h-5 text-gray-500" data-id="element-1689" data-mp="3tkfcgwey7mz7" />}
                </button>
                {expandedSections.brands && <div className="px-4 pb-4" data-id="element-1690" data-mp="3omw0621t9k9w">
                    <input type="text" placeholder="Search brands" className="w-full p-2 mb-3 border border-gray-300 rounded-md text-sm" data-id="element-1691" data-mp="74kwn6k03c4s" />
                    <div className="space-y-3 max-h-48 overflow-y-auto" data-id="element-1692" data-mp="g4hplkdfax5n">
                      {brands.map(brand => <label key={brand} className="flex items-center cursor-pointer" data-id="element-1693" data-mp="b5wmnd6sdzdi">
                          <input type="checkbox" checked={selectedFilters.brands.includes(brand)} onChange={() => handleBrandToggle(brand)} className="w-5 h-5 text-[#0066CC] border-gray-300 rounded" data-id="element-1694" data-mp="l4cfplpdjipn" />
                          <span className="ml-3 text-sm text-gray-700" data-id="element-1695" data-mp="12eq42enk329j">
                            {brand}
                          </span>
                        </label>)}
                    </div>
                  </div>}
              </div>
              {/* Category Filter */}
              <div className="border-b border-gray-100" data-id="element-1696" data-mp="xkvc6nxsrgzd">
                <button onClick={() => toggleSection('categories')} className="flex justify-between items-center w-full p-4 text-left" data-id="element-1697" data-mp="jmnjwtbph3uj">
                  <h3 className="text-base font-bold text-gray-900" data-id="element-1698" data-mp="9qas60zq102f">
                    Categories
                  </h3>
                  {expandedSections.categories ? <ChevronDownIcon className="w-5 h-5 text-gray-500" data-id="element-1699" data-mp="243jvtc5l5rcr" /> : <ChevronRightIcon className="w-5 h-5 text-gray-500" data-id="element-1700" data-mp="2924tb3tgdwxi" />}
                </button>
                {expandedSections.categories && <div className="px-4 pb-4" data-id="element-1701" data-mp="vxmn111i4fbn">
                    <div className="space-y-3" data-id="element-1702" data-mp="rtor0z7h3kmt">
                      {categories.map(category => <label key={category} className="flex items-center cursor-pointer" data-id="element-1703" data-mp="n26o45lw39z7">
                          <input type="checkbox" checked={selectedFilters.categories.includes(category)} onChange={() => handleCategoryToggle(category)} className="w-5 h-5 text-[#0066CC] border-gray-300 rounded" data-id="element-1704" data-mp="d4skj6l2efob" />
                          <span className="ml-3 text-sm text-gray-700" data-id="element-1705" data-mp="1yiwxtypiujxk">
                            {category}
                          </span>
                        </label>)}
                    </div>
                  </div>}
              </div>
              {/* Prescription Toggle */}
              <div className="border-b border-gray-100 p-4" data-id="element-1706" data-mp="260lf08z6rbrx">
                <label className="flex items-center justify-between cursor-pointer" data-id="element-1707" data-mp="2asv1tg507vpz">
                  <span className="text-base font-bold text-gray-900" data-id="element-1708" data-mp="2frpst5d4kql6">
                    Needs Prescription
                  </span>
                  <div className="relative" data-id="element-1709" data-mp="1i4qukq6sbal6">
                    <input type="checkbox" checked={selectedFilters.needsPrescription} onChange={e => onFilterChange({
                  ...selectedFilters,
                  needsPrescription: e.target.checked
                })} className="sr-only peer" data-id="element-1710" data-mp="1i61xknseoeb8" />
                    <div className="w-11 h-6 bg-gray-200 rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#0066CC]" data-id="element-1711" data-mp="37iccyamra9z2"></div>
                  </div>
                </label>
              </div>
              {/* Offers Filter */}
              <div className="border-b border-gray-100" data-id="element-1712" data-mp="1g92qv8975gfd">
                <button onClick={() => toggleSection('offers')} className="flex justify-between items-center w-full p-4 text-left" data-id="element-1713" data-mp="1gam6qxqxxpgm">
                  <h3 className="text-base font-bold text-gray-900" data-id="element-1714" data-mp="nk48qb52qea2">Offers</h3>
                  {expandedSections.offers ? <ChevronDownIcon className="w-5 h-5 text-gray-500" data-id="element-1715" data-mp="xi1umrzgnyzi" /> : <ChevronRightIcon className="w-5 h-5 text-gray-500" data-id="element-1716" data-mp="xfp6ty70uuej" />}
                </button>
                {expandedSections.offers && <div className="px-4 pb-4" data-id="element-1717" data-mp="28vnjec1eryki">
                    <div className="space-y-3" data-id="element-1718" data-mp="3tezq38l3w3kt">
                      {offers.map(offer => <label key={offer} className="flex items-center cursor-pointer" data-id="element-1719" data-mp="3ogeob4q7hbu9">
                          <input type="checkbox" checked={selectedFilters.offers.includes(offer)} onChange={() => handleOfferToggle(offer)} className="w-5 h-5 text-[#0066CC] border-gray-300 rounded" data-id="element-1720" data-mp="3jnd54kiyuq4v" />
                          <span className="ml-3 text-sm text-gray-700" data-id="element-1721" data-mp="30q26n255s0qh">
                            {offer}
                          </span>
                        </label>)}
                    </div>
                  </div>}
              </div>
            </div>
            {/* Footer Actions - Sticky at bottom of sheet */}
            <div className="bg-white border-t border-gray-200 p-4 flex gap-3 flex-shrink-0 safe-area-bottom" data-id="element-1722" data-mp="ty1em2i31w5h">
              <button onClick={handleReset} className="flex-1 py-3 px-4 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50" data-id="element-1723" data-mp="2oehxq75tqi9e">
                Clear All
              </button>
              <button onClick={onClose} className="flex-1 py-3 px-4 bg-[#0066CC] text-white rounded-lg text-sm font-medium hover:bg-[#0052a3]" data-id="element-1724" data-mp="2jiay0w40u2zw">
                Apply Filters
              </button>
            </div>
          </motion.div>
        </>}
    </AnimatePresence>;
}